<template>
  <div class="nfe-shell">
    <div class="nfe-head">
      <h2 class="nfe-title">Importar Nota Fiscal</h2>
      <button class="btn-back" type="button" @click="$emit('cancel')">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        Voltar
      </button>
    </div>

    <!-- Scanner input -->
    <div v-if="!nfData" class="scan-section">
      <div class="scan-hint">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 9V5a2 2 0 0 1 2-2h4"/><path d="M21 9V5a2 2 0 0 0-2-2h-4"/><path d="M3 15v4a2 2 0 0 0 2 2h4"/><path d="M21 15v4a2 2 0 0 0-2 2h-4"/><line x1="7" y1="12" x2="7" y2="12"/><line x1="12" y1="12" x2="12" y2="12"/><line x1="17" y1="12" x2="17" y2="12"/></svg>
        <span>Escaneie o código de barras do DANFE ou digite a chave de acesso (44 dígitos)</span>
      </div>
      <div class="scan-row">
        <input
          ref="barcodeInput"
          class="scan-input"
          v-model="barcode"
          placeholder="Chave de acesso NFe (44 dígitos)"
          @keyup.enter="lookup"
          maxlength="50"
        />
        <button class="btn-scan" type="button" :disabled="looking" @click="lookup">
          {{ looking ? 'Consultando...' : 'Consultar' }}
        </button>
      </div>
      <p v-if="lookupError" class="error-text">{{ lookupError }}</p>
    </div>

    <!-- NF items review -->
    <div v-if="nfData" class="nf-review">
      <div class="nf-summary">
        <div class="summary-row">
          <span class="summary-label">Emitente:</span>
          <span class="summary-val">{{ nfData.emitenteNome || nfData.emitenteCNPJ || '—' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Chave:</span>
          <span class="summary-val summary-val--small">{{ nfData.chave }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Itens encontrados:</span>
          <span class="summary-val">{{ nfData.items.length }}</span>
        </div>
        <button class="btn-rescan" type="button" @click="reset">Escanear outra NF</button>
      </div>

      <div class="items-list">
        <div v-for="(item, idx) in reviewItems" :key="idx" class="nf-item-card">
          <div class="item-desc">{{ item.description }}</div>
          <div class="item-fields">
            <label class="item-field">
              <span class="item-field-label">Quantidade</span>
              <input class="item-input" type="number" v-model.number="item.quantidade" min="0" step="0.01" />
            </label>
            <label class="item-field">
              <span class="item-field-label">Valor unitário (R$)</span>
              <input class="item-input" type="number" v-model.number="item.valorUnitario" min="0" step="0.01" />
            </label>
            <div class="item-field item-field--full">
              <span class="item-field-label">Vincular ao estoque</span>
              <select class="item-select" v-model="item.warehouseItemId">
                <option value="">— Criar novo item —</option>
                <option v-for="wi in warehouseItems" :key="wi.id" :value="wi.id">{{ wi.name }}</option>
              </select>
            </div>
            <label v-if="!item.warehouseItemId" class="item-field item-field--checkbox">
              <input type="checkbox" v-model="item.criarNovoItem" />
              <span>Criar novo item no estoque com esta descrição</span>
            </label>
          </div>
        </div>
      </div>

      <p v-if="importError" class="error-text">{{ importError }}</p>
      <div class="import-actions">
        <button class="btn-confirm" type="button" :disabled="importing" @click="confirmImport">
          {{ importing ? 'Importando...' : 'Confirmar Importação' }}
        </button>
        <button class="btn-cancel" type="button" @click="$emit('cancel')">Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { apiFetch } from '@/store/api'
import { warehouseStore } from '@/store/warehouse'
import { uiStore } from '@/store/ui'

interface NfItem { description: string; quantity: number; unitValue: number }
interface ReviewItem { description: string; quantidade: number; valorUnitario: number; warehouseItemId: string; criarNovoItem: boolean; ncm: string; cean: string }

export default defineComponent({
  name: 'WarehouseNFeImportForm',
  emits: ['cancel', 'done'],
  setup(_, { emit }) {
    const barcodeInput = ref<HTMLInputElement>()
    const barcode = ref('')
    const looking = ref(false)
    const lookupError = ref('')
    const nfData = ref<{ chave: string; emitenteNome?: string; emitenteCNPJ?: string; xml: string; items: NfItem[] } | null>(null)
    const reviewItems = ref<ReviewItem[]>([])
    const importing = ref(false)
    const importError = ref('')
    const warehouseItems = computed(() => warehouseStore.all())

    const lookup = async () => {
      const code = barcode.value.replace(/\D/g, '')
      if (code.length < 10) { lookupError.value = 'Digite pelo menos os primeiros dígitos da chave NFe.'; return }
      looking.value = true; lookupError.value = ''
      try {
        const data = await apiFetch<{ ok: boolean; chave: string; items: NfItem[]; xml: string; emitenteNome?: string; emitenteCNPJ?: string }>('/warehouse/danfe/lookup', {
          method: 'POST', body: JSON.stringify({ barcode: code })
        })
        nfData.value = data
        reviewItems.value = data.items.map(it => ({
          description: it.description, quantidade: it.quantity, valorUnitario: it.unitValue,
          warehouseItemId: '', criarNovoItem: true, ncm: '', cean: ''
        }))
        if (!warehouseStore.all().length) await warehouseStore.refresh()
      } catch (e: unknown) { lookupError.value = e instanceof Error ? e.message : 'Erro ao consultar NF.' }
      finally { looking.value = false }
    }

    const reset = () => { nfData.value = null; barcode.value = ''; lookupError.value = ''; reviewItems.value = [] }

    const confirmImport = async () => {
      if (!nfData.value) return
      importing.value = true; importError.value = ''
      try {
        const result = await apiFetch<{ ok: boolean; nfId: string }>('/warehouse/nfe-import', {
          method: 'POST',
          body: JSON.stringify({
            chave: nfData.value.chave,
            items: reviewItems.value.map(it => ({
              descricao: it.description, quantidade: it.quantidade, valorUnitario: it.valorUnitario,
              warehouseItemId: it.warehouseItemId, criarNovoItem: it.criarNovoItem && !it.warehouseItemId,
              ncm: it.ncm, cean: it.cean
            }))
          })
        })
        uiStore.toast('Nota Fiscal importada com sucesso!', 'success')
        emit('done', result.nfId)
      } catch (e: unknown) { importError.value = e instanceof Error ? e.message : 'Erro ao importar NF.' }
      finally { importing.value = false }
    }

    onMounted(() => {
      barcodeInput.value?.focus()
      if (!warehouseStore.all().length) warehouseStore.refresh().catch(() => { /* ignore */ })
    })

    return { barcodeInput, barcode, looking, lookupError, nfData, reviewItems, warehouseItems, importing, importError, lookup, reset, confirmImport }
  }
})
</script>

<style scoped lang="scss">
.nfe-shell { width: 100%; }
.nfe-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.nfe-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-back {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px;
  border: 1.5px solid rgba(31,30,26,.18); background: #fff; cursor: pointer; font-size: 13px; font-weight: 500;
  &:hover { background: rgba(31,30,26,.04); }
}
.scan-section { display: flex; flex-direction: column; gap: 16px; max-width: 600px; }
.scan-hint {
  display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: #f0f5ff;
  border-radius: 10px; border: 1px solid rgba(15,76,129,.15); color: #0f4c81; font-size: 13px;
  svg { flex-shrink: 0; opacity: .7; }
}
.scan-row { display: flex; gap: 8px; }
.scan-input {
  flex: 1; padding: 10px 14px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.18);
  font-size: 14px; outline: none;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.btn-scan {
  padding: 10px 20px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .6; cursor: not-allowed; }
}
.error-text { font-size: 13px; color: #dc2626; margin: 4px 0 0; }
.nf-review { display: flex; flex-direction: column; gap: 20px; }
.nf-summary {
  background: #f8f7f5; border: 1px solid rgba(31,30,26,.1); border-radius: 10px; padding: 14px 16px;
  display: flex; flex-direction: column; gap: 6px;
}
.summary-row { display: flex; gap: 8px; align-items: baseline; }
.summary-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); min-width: 100px; }
.summary-val { font-size: 13px; font-weight: 600; color: var(--ink, #2c2b27); &--small { font-size: 11px; font-family: monospace; word-break: break-all; } }
.btn-rescan {
  align-self: flex-start; margin-top: 4px; padding: 6px 12px; border-radius: 6px;
  border: 1.5px solid rgba(31,30,26,.15); background: #fff; cursor: pointer; font-size: 12px;
  &:hover { background: rgba(31,30,26,.04); }
}
.items-list { display: flex; flex-direction: column; gap: 12px; }
.nf-item-card { border: 1px solid rgba(31,30,26,.1); border-radius: 10px; background: var(--card, #fff); padding: 14px 16px; }
.item-desc { font-size: 14px; font-weight: 600; color: var(--ink, #2c2b27); margin-bottom: 12px; }
.item-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.item-field {
  display: flex; flex-direction: column; gap: 4px;
  &--full { grid-column: 1 / -1; }
  &--checkbox { flex-direction: row; align-items: center; gap: 8px; font-size: 13px; color: var(--ink-muted, #666); }
}
.item-field-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); }
.item-input { padding: 7px 10px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.15); font-size: 13px; outline: none; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.item-select { padding: 7px 10px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.15); font-size: 13px; outline: none; background: #fff; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.import-actions { display: flex; gap: 10px; }
.btn-confirm {
  padding: 10px 20px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 700;
  &:disabled { opacity: .6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: .88; }
}
.btn-cancel {
  padding: 10px 16px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.18);
  background: #fff; cursor: pointer; font-size: 13px;
  &:hover { background: rgba(31,30,26,.04); }
}
@media (max-width: 600px) { .item-fields { grid-template-columns: 1fr; } }
</style>
